import { ShieldCheck, Info } from 'lucide-react';

/**
 * FIRL Compliance + Non-Enforcement banner.
 *
 * Surfaces the canonical FIRL invariants on every admin surface so reviewers
 * always see (a) FIRL is AI-controlled routing/validation/enforcement —
 * not a policy-making authority, (b) all decisions trace to existing
 * statutory/regulatory authority, (c) compliance evidence flows through
 * the Hub audit ledger and the FIRL-scoped append-only ledger automatically.
 *
 * Use the `compact` variant on dense list pages; the default (full) variant
 * on entry / landing pages.
 */
export default function FirlComplianceBanner({ compact = false, className = '' }) {
  if (compact) {
    return (
      <div
        role="note"
        aria-label="FIRL compliance notice"
        className={`mb-5 flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-900 ${className}`}
      >
        <Info className="h-3.5 w-3.5 flex-none" aria-hidden />
        <span>
          <strong className="font-semibold">AI-assisted, human-accountable.</strong>{' '}
          Every gate decision references existing statutory authority and is captured in the
          append-only FIRL ledger.
        </span>
      </div>
    );
  }

  return (
    <div
      role="note"
      aria-label="FIRL compliance notice"
      className={`mb-6 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50/70 px-4 py-3 text-sm text-emerald-900 ${className}`}
    >
      <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-emerald-700" aria-hidden />
      <div className="space-y-1">
        <p className="font-semibold">Policy-constrained, human-accountable.</p>
        <p className="text-emerald-900/90">
          FIRL is AI-controlled routing, validation, and enforcement — not a policy-making
          authority. Every decision traces to existing statutory, regulatory, or
          agency-delegated authority. Disbursement, suspension, termination, recovery, and
          referral actions are captured in an append-only FIRL ledger with compliance
          crosswalk references (PIIA / OMB&nbsp;A-123 / Do Not Pay / DATA Act / FFATA / GAO).
        </p>
      </div>
    </div>
  );
}
